import projects from '@/data/projects';
import useMethodStore from '@/store/MethodStore';
import { useState, useEffect, useRef, useCallback, KeyboardEvent, useMemo } from 'react';

interface Command {
  name: string;
  description: string;
  action?: (args?: string[]) => string | string[];
  hidden?: boolean;
}

const asciiArt = `
██████╗ ██╗  ██╗██╗   ██╗ █████╗ ███╗   ██╗██╗████████╗
██╔══██╗██║  ██║██║   ██║██╔══██╗████╗  ██║██║╚══██╔══╝
██║  ██║███████║██║   ██║███████║██╔██╗ ██║██║   ██║   
██║  ██║██╔══██║╚██╗ ██╔╝██╔══██║██║╚██╗██║██║   ██║   
██████╔╝██║  ██║ ╚████╔╝ ██║  ██║██║ ╚████║██║   ██║   
╚═════╝ ╚═╝  ╚═╝  ╚═══╝  ╚═╝  ╚═╝╚═╝  ╚═══╝╚═╝   ╚═╝   
`;

const TerminalApp = () => {

  const isMediumScreen = window.matchMedia("(max-width: 768px)").matches

  const terminalState = useMethodStore(state => state.terminalState);
  const [input, setInput] = useState<string>('');
  const [history, setHistory] = useState<string[]>([`${!isMediumScreen && asciiArt} \nWelcome to LackOS Terminal v2.1.1\nType 'help' for all commands`]);
  const [commandIndex, setCommandIndex] = useState<number>(-1);
  const terminalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [crtEffect, setCrtEffect] = useState<boolean>(true);
  const [theme, setTheme] = useState<'green' | 'amber' | 'matrix'>('green');

  // Command definitions
  const commands: Command[] = useMemo(() => [
    {
      name: 'help',
      description: 'Display available commands',
      action: () => commands
        .filter(cmd => !cmd.hidden)
        .map(cmd => `${cmd.name.padEnd(15)} ${cmd.description}`)
        .join('\n')
    },
    {
      name: 'about',
      description: 'Show developer information',
      action: () => [
        'LackOS Terminal v2.1.1',
        'Developed by Dhvanit Monpara',
        'Type "projects" to see my work',
        'Hardware: Quantum CPU @ 5.6THz | 128PB RAM'
      ].join('\n')
    },
    {
      name: 'clear',
      description: 'Clear terminal history',
      action: () => {
        setHistory([]);
        return '';
      }
    },
    {
      name: 'projects',
      description: 'List my featured projects',
      action: () => {
        const processedProjects = projects.map(({ title, description }) => (
          `${title} - ${description}`
        ))
        return processedProjects.join('\n')
      }
    },
    {
      name: 'date',
      description: 'Show current date and time',
      action: () => new Date().toLocaleString()
    },
    {
      name: 'neofetch',
      description: 'Display system information',
      action: () => [
        'lackOS v2.1.1',
        '----------------',
        'OS: LackOS Quantum Edition',
        'Shell: zsh 5.9',
        'CPU: Quantum Core i9-14900K @ 5.6THz',
        'GPU: RTX 9090 Ti (Teraflop Edition)',
        'Memory: 128PB DDR6',
        'Disk: 1YB NVMe (Holographic Storage)'
      ].join('\n')
    },
    {
      name: 'theme',
      description: 'Change terminal color theme',
      action: (args?: string[]) => {
        const validThemes = ['green', 'amber', 'matrix'];
        if (!args?.length) return `Current theme: ${theme}. Available: ${validThemes.join(', ')}\ntheme <command> : to change theme`;
        const newTheme = args[0].toLowerCase();
        if (validThemes.includes(newTheme)) {
          setTheme(newTheme as typeof theme);
          return `Theme changed to ${newTheme}`;
        }
        return `Invalid theme. Available: ${validThemes.join(', ')}`;
      }
    },
    {
      name: 'matrix',
      description: 'Enable matrix mode',
      action: () => {
        terminalRef.current?.classList.add('matrix-effect');
        setTimeout(() => terminalRef.current?.classList.remove("matrix-effect"), 5000);
        return 'Protocol: GreenRain_v1.62 initialized';
      }
    },
    {
      name: 'secret',
      description: '???',
      hidden: true,
      action: () => [
        '> Initializing quantum decryption...',
        '> Accessing secret layer...',
        '████ 10%',
        '██████████ 50%',
        '██████████████████ 100%',
        'Welcome to level 7 access:',
        'Easter egg found! 🥚',
        'Portfolio vulnerability: 0%',
        'Coffee supply: Infinite ☕'
      ]
    },
    {
      name: 'sudo',
      description: 'Execute privileged command',
      hidden: true,
      action: (args?: string[]) => {
        if (!args?.length) return 'Usage: sudo <command>';
        const cmd = args.join(' ');
        if (cmd === 'rm -rf /') return [
          '[WARNING] System wipe prevented!',
          'Nice try! 😉'
        ];
        if (cmd === 'make me a sandwich') return [
          'What? Make it yourself.',
          '*poof* Sandwich appears! 🥪'
        ];
        return `Command '${cmd}' not allowed`;
      }
    }
  ], [theme]);

  const processCommand = useCallback((command: string) => {
    const args = command.trim().split(/\s+/);
    const baseCmd = args[0].toLowerCase();
    const fullCommand = commands.find(c => c.name === baseCmd);
    let output: string[] = [];

    if (fullCommand?.action) {
      const result = fullCommand.action(args.slice(1));
      output = typeof result === "string" ? [result] : result;
    } else {
      const lcCommand = command.toLowerCase();
      if (lcCommand === 'hello world') {
        output = ['Hello, developer! 👋'];
      } else if (lcCommand === 'the answer to life the universe and everything') {
        output = ['42'];
      } else if (lcCommand === 'hack the planet') {
        output = ['Hack failed. Try harder! 💻'];
      } else if (lcCommand === 'open sesame') {
        output = ['Access denied. 🚫'];
      } else {
        const suggestions = commands
          .filter(c => !c.hidden && c.name.startsWith(baseCmd))
          .map(c => c.name);
        output = [`Command not found: ${command}`];
        if (suggestions.length) output.push(`Did you mean: ${suggestions.join(', ')}?`);
      }
    }

    setHistory(prev => [...prev, `$ ${command}`, ...output]);
    setInput('');
    setCommandIndex(-1);
  }, [commands]);

  // Command history navigation
  useEffect(() => {
    const historyCommands = history
      .filter(entry => entry.startsWith('$ '))
      .map(entry => entry.slice(2))
      .reverse();

    if (commandIndex >= 0 && commandIndex < historyCommands.length) {
      setInput(historyCommands[commandIndex]);
    }
  }, [commandIndex, history]);

  const handleInput = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
        processCommand(input);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setCommandIndex(prev => Math.min(prev + 1, history.length));
        break;
      case 'ArrowDown':
        e.preventDefault();
        setCommandIndex(prev => Math.max(prev - 1, -1));
        break;
      case 'Tab': {
        e.preventDefault();
        const matches = commands.filter(c =>
          !c.hidden && c.name.startsWith(input.toLowerCase())
        ).map(c => c.name);
        if (matches.length === 1) setInput(matches[0] + ' ');
        break;
      }
    }
  };

  useEffect(() => {
    terminalRef.current?.scrollTo(0, terminalRef.current.scrollHeight);
  }, [history]);

  return (
    <div className={`${terminalState === "maximized" ? "w-full h-full" : "h-full w-full lg:w-[700px] lg:h-[550px]"} 
      bg-black/90 ${theme === 'green' ? 'text-green-400' : theme === 'amber' ? 'text-amber-400' : 'text-green-300'} 
      font-mono rounded-lg overflow-hidden border-2 ${theme === 'green' ? 'border-green-500/30' :
        theme === 'amber' ? 'border-amber-500/30' : 'border-green-500/30'} relative ${crtEffect ? 'crt-effect' : ''}`}>

      <div className={`${theme === 'green' ? 'bg-green-900/20' :
        theme === 'amber' ? 'bg-amber-900/20' : 'bg-green-800/20'} p-2 border-b ${theme === 'green' ?
          'border-green-500/30' : theme === 'amber' ? 'border-amber-500/30' : 'border-green-500/30'} flex items-center 
        justify-between`}>
        <span className="text-sm">LackOS Terminal</span>
        <button
          onClick={() => setCrtEffect(!crtEffect)}
          className="text-sm hover:text-green-300 transition-colors"
        >
          CRT {crtEffect ? 'ON' : 'OFF'}
        </button>
      </div>

      <div ref={terminalRef} className="h-[calc(100%-40px)] p-4 overflow-y-auto scroll2 terminal-scroll">
        {history.map((entry, index) => (
          <pre key={index} className="whitespace-pre-wrap break-words mb-1">
            {entry}
          </pre>
        ))}
        <div className="flex items-center gap-2 mt-2">
          <span className={`${theme === 'green' ? 'text-yellow-500' :
            theme === 'amber' ? 'text-amber-500' : 'text-green-500'}`}>➜</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleInput}
            className={`w-full bg-transparent outline-none caret-green-500 ${theme === 'green' ?
              'text-green-400' : theme === 'amber' ? 'text-amber-400' : 'text-green-300'}`}
            placeholder="Enter command..."
            autoFocus
          />
        </div>
      </div>
    </div>
  );
};

export default TerminalApp;